import { useState, useEffect, useMemo } from "react";
import AppBreadcrumbs from "../../../components/AppBreadcrumbs";
import * as services from "../../../services/saleng";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import SearchIcon from "@mui/icons-material/Search";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CloseIcon from "@mui/icons-material/Close";
import { getAdjustedPurchaseItems } from "../../../utils/adjustPurchaseItems";

export default function SummaryPurchasesProduct() {
  const formatDate = (date) => date.toISOString().split("T")[0];
  const [startDate, setStartDate] = useState(() => {
    const date = new Date();
    date.setMonth(date.getMonth() - 1);
    return formatDate(date);
  });
  const [endDate, setEndDate] = useState(formatDate(new Date()));
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [groupedDetails, setGroupedDetails] = useState({});

  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("title");

  const [selectedItemKey, setSelectedItemKey] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [filterText, setFilterText] = useState("");

  const selectedItem = rows.find((r) => r.key === selectedItemKey);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const res = await services.getPurchaseTransactions({
          status: "payed",
          startDate,
          endDate,
          dateField: "paidAt",
        });
        const purchaseTrans = res.data?.data || [];

        const itemMap = {};
        const detailsMap = {};

        purchaseTrans.forEach((trans) => {
          getAdjustedPurchaseItems(trans).forEach((item) => {
            const { netWeight: weight, amountBeforeVat: amount } = item;
            const productId = item.productId ?? item.product?.id ?? item.id;
            if (productId == null || (weight <= 0 && amount === 0)) return;
            const key = `${productId}`;
            const unitPrice = Number(item.pricePerUnit ?? 0);

            if (!itemMap[key]) {
              itemMap[key] = {
                key,
                code: item.product?.code ?? "",
                title: item.product?.title || item.productName || "-",
                category: item.product?.category?.name || "-",
                sub_category: item.product?.subCategory?.name || "-",
                unit: "กก.",
                totalWeight: 0,
                totalAmount: 0,
                totalCost: 0,
                count: 0,
              };
              detailsMap[key] = [];
            }

            itemMap[key].totalWeight += weight;
            itemMap[key].totalAmount += amount;
            itemMap[key].totalCost += unitPrice;
            itemMap[key].count += 1;

            detailsMap[key].push({
              ...item,
              poNumber: trans.billNumber,
              poDate: trans.createdAt,
              supplier: trans.customer?.name || "-",
              netWeight: weight,
              pricePerUnit: unitPrice,
              amountBeforeVat: amount,
            });
          });
        });

        const grouped = Object.values(itemMap).map((item) => ({
          ...item,
          weight: item.totalWeight,
          average_cost:
            item.totalWeight > 0 ? item.totalAmount / item.totalWeight : 0,
          amount_before_vat: item.totalAmount,
        }));

        setRows(grouped);
        setGroupedDetails(detailsMap);
      } catch (err) {
        console.error("Fetch error:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [startDate, endDate]);

  const handleSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const getComparator = (order, orderBy) =>
    order === "desc"
      ? (a, b) => descendingComparator(a, b, orderBy)
      : (a, b) => -descendingComparator(a, b, orderBy);

  const descendingComparator = (a, b, orderBy) => {
    const av = a?.[orderBy];
    const bv = b?.[orderBy];
    if (typeof av === "number" && typeof bv === "number") {
      if (bv < av) return -1;
      if (bv > av) return 1;
      return 0;
    }
    const as = (av ?? "").toString();
    const bs = (bv ?? "").toString();
    return bs.localeCompare(as, "th");
  };

  const sortData = (array, comparator) => {
    const stabilized = array.map((el, index) => [el, index]);
    stabilized.sort((a, b) => {
      const cmp = comparator(a[0], b[0]);
      if (cmp !== 0) return cmp;
      return a[1] - b[1];
    });
    return stabilized.map((el) => el[0]);
  };

  const formatNum = (value) =>
    new Intl.NumberFormat("th-TH", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(Number(value || 0));

  function exportToCSV(data, filename = "data.csv") {
    if (!data || data.length === 0) return;
    const headers = Object.keys(data[0]);
    const rows = data.map((row) =>
      headers.map((field) => `"${row[field] ?? ""}"`).join(",")
    );
    const csv = [headers.join(","), ...rows].join("\n");
    const blob = new Blob(["\uFEFF" + csv], {
      type: "text/csv;charset=utf-8;",
    });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  const filteredRows = useMemo(() => {
    const term = (filterText || "").toLowerCase();
    return rows.filter((row) =>
      (row.title || "").toLowerCase().includes(term) ||
      ((row.code ?? "") + "").toLowerCase().includes(term) ||
      (row.category || "").toLowerCase().includes(term) ||
      (row.sub_category || "").toLowerCase().includes(term)
    );
  }, [rows, filterText]);

  const summary = useMemo(() => {
    const totalWeight = filteredRows.reduce(
      (acc, r) => acc + (r.weight || 0),
      0
    );
    const totalAmountBeforeVat = filteredRows.reduce(
      (acc, r) => acc + (r.amount_before_vat || 0),
      0
    );
    const weightedAvgCost =
      totalWeight > 0 ? totalAmountBeforeVat / totalWeight : 0;

    return {
      count: filteredRows.length,
      totalWeight,
      weightedAvgCost,
      totalAmountBeforeVat,
    };
  }, [filteredRows]);

  const sortedRows = sortData(filteredRows, getComparator(order, orderBy));

  return (
    <div className="min-h-screen bg-gray-50 p-4 font-sans">
      <div className="max-w-7xl mx-auto">
        <div className="mb-6">
          <AppBreadcrumbs />
        </div>

        {/* Header & Controls */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-6">
          <div className="flex flex-col xl:flex-row justify-between items-start xl:items-center gap-4">
            <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2 mb-2 xl:mb-0">
               <span className="w-1 h-8 bg-green-600 rounded-full"></span>
               สรุปยอดซื้อสินค้ารายชนิด
            </h1>

            <div className="flex flex-col lg:flex-row items-start lg:items-center gap-3 w-full xl:w-auto">
               {/* Search */}
               <div className="relative w-full lg:w-80">
                <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                    type="text"
                    placeholder="ชื่อสินค้า / รหัส / หมวดหมู่"
                    value={filterText}
                    onChange={(e) => setFilterText(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none transition-all"
                />
               </div>

              {/* Date Pickers */}
              <div className="flex items-center gap-2 bg-gray-50 px-3 py-2 rounded-xl border border-gray-200 w-full lg:w-auto">
                <span className="text-sm font-medium text-gray-500 whitespace-nowrap">เริ่ม</span>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="bg-transparent border-none focus:ring-0 text-gray-700 font-medium text-sm w-full"
                />
              </div>

               <div className="flex items-center gap-2 bg-gray-50 px-3 py-2 rounded-xl border border-gray-200 w-full lg:w-auto">
                <span className="text-sm font-medium text-gray-500 whitespace-nowrap">สิ้นสุด</span>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="bg-transparent border-none focus:ring-0 text-gray-700 font-medium text-sm w-full"
                />
              </div>
            </div>
          </div>
          
           <div className="mt-6 flex gap-2 justify-end">
                 <button
                    onClick={() => {
                        const transformedData = rows.map((row) => ({
                          รหัสสินค้า: row.code,
                          ชื่อสินค้า: row.title,
                          หมวดหมู่หลัก: row.category,
                          หมวดหมู่ย่อย: row.sub_category,
                          "น้ำหนัก (กก.)": (row.weight ?? 0).toFixed(2),
                          หน่วย: "กก.",
                          ต้นทุนเฉลี่ย: (row.average_cost ?? 0).toFixed(2),
                          ราคารวมก่อนภาษี: (row.amount_before_vat ?? 0).toFixed(2),
                        }));
                        exportToCSV(transformedData, "summary.csv");
                    }}
                    className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white border border-transparent rounded-xl hover:bg-green-700 transition-all font-medium shadow-sm hover:shadow-md"
                >
                    <ContentCopyIcon fontSize="small" />
                    <span>Export ข้อมูล</span>
                </button>
          </div>
        </div>

        {/* Content */}
         {loading ? (
             <div className="flex justify-center items-center h-64">
                <div className="w-12 h-12 border-4 border-green-200 border-t-green-600 rounded-full animate-spin"></div>
             </div>
        ) : (
             <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                 <div className="overflow-x-auto">
                     <table className="w-full text-sm text-left">
                         <thead className="bg-gray-50 text-gray-600 font-semibold border-b border-gray-100">
                             <tr>
                                <th className="px-4 py-4 w-16"></th>
                                <th onClick={() => handleSort("code")} className="px-4 py-4 cursor-pointer hover:bg-gray-100">
                                     <div className="flex items-center gap-1">
                                        รหัสสินค้า {orderBy === "code" && <span className="text-green-600">{order === "asc" ? "↑" : "↓"}</span>}
                                    </div>
                                </th>
                                <th onClick={() => handleSort("title")} className="px-4 py-4 cursor-pointer hover:bg-gray-100">
                                     <div className="flex items-center gap-1">
                                        สินค้า {orderBy === "title" && <span className="text-green-600">{order === "asc" ? "↑" : "↓"}</span>}
                                    </div>
                                </th>
                                <th onClick={() => handleSort("category")} className="px-4 py-4 cursor-pointer hover:bg-gray-100">
                                     <div className="flex items-center gap-1">
                                        หมวดหมู่หลัก {orderBy === "category" && <span className="text-green-600">{order === "asc" ? "↑" : "↓"}</span>}
                                    </div>
                                </th>
                                <th onClick={() => handleSort("sub_category")} className="px-4 py-4 cursor-pointer hover:bg-gray-100">
                                     <div className="flex items-center gap-1">
                                        หมวดหมู่ย่อย {orderBy === "sub_category" && <span className="text-green-600">{order === "asc" ? "↑" : "↓"}</span>}
                                    </div>
                                </th>
                                <th onClick={() => handleSort("weight")} className="px-4 py-4 text-right cursor-pointer hover:bg-gray-100">
                                     <div className="flex items-center justify-end gap-1">
                                        น้ำหนัก {orderBy === "weight" && <span className="text-green-600">{order === "asc" ? "↑" : "↓"}</span>}
                                    </div>
                                </th>
                                <th className="px-4 py-4">หน่วย</th>
                                <th onClick={() => handleSort("average_cost")} className="px-4 py-4 text-right cursor-pointer hover:bg-gray-100">
                                     <div className="flex items-center justify-end gap-1">
                                        ต้นทุนเฉลี่ย {orderBy === "average_cost" && <span className="text-green-600">{order === "asc" ? "↑" : "↓"}</span>}
                                    </div>
                                </th>
                                <th onClick={() => handleSort("amount_before_vat")} className="px-4 py-4 text-right cursor-pointer hover:bg-gray-100">
                                     <div className="flex items-center justify-end gap-1">
                                        ราคารวมก่อนภาษี {orderBy === "amount_before_vat" && <span className="text-green-600">{order === "asc" ? "↑" : "↓"}</span>}
                                    </div>
                                </th>
                             </tr>
                         </thead>
                         <tbody className="divide-y divide-gray-50">
                             {sortedRows.map((row) => (
                                 <tr key={row.key} className="hover:bg-gray-50 transition-colors">
                                     <td className="px-4 py-3">
                                         <button 
                                            onClick={() => {
                                                setSelectedItemKey(row.key);
                                                setModalOpen(true);
                                            }}
                                            className="p-1.5 text-gray-500 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                                        >
                                            <VisibilityIcon fontSize="small" />
                                        </button>
                                     </td>
                                     <td className="px-4 py-3 text-gray-600">{row.code}</td>
                                     <td className="px-4 py-3 font-medium text-gray-800">{row.title}</td>
                                     <td className="px-4 py-3 text-gray-600">{row.category}</td>
                                     <td className="px-4 py-3 text-gray-600">{row.sub_category}</td>
                                     <td className="px-4 py-3 text-right font-medium">{formatNum(row.weight)}</td>
                                     <td className="px-4 py-3 text-gray-500">{row.unit}</td>
                                     <td className="px-4 py-3 text-right text-gray-600">{formatNum(row.average_cost)}</td>
                                     <td className="px-4 py-3 text-right font-bold text-gray-800">{formatNum(row.amount_before_vat)}</td>
                                 </tr>
                             ))}
                         </tbody>
                         <tfoot className="bg-gray-50 font-bold text-gray-900 border-t border-gray-200">
                             <tr>
                                 <td colSpan={5} className="px-4 py-4 text-right">ยอดรวม</td>
                                 <td className="px-4 py-4 text-right">{formatNum(summary.totalWeight)}</td>
                                 <td className="px-4 py-4"></td>
                                 <td className="px-4 py-4 text-right">{formatNum(summary.weightedAvgCost)}</td>
                                 <td className="px-4 py-4 text-right text-lg text-green-700">{formatNum(summary.totalAmountBeforeVat)}</td>
                             </tr>
                         </tfoot>
                     </table>
                 </div>
             </div>
        )}

        {/* Modal */}
        {modalOpen && (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setModalOpen(false)}></div>
                <div className="bg-white rounded-2xl shadow-xl w-full max-w-4xl z-10 overflow-hidden flex flex-col max-h-[90vh]">
                     {/* Modal Header */}
                    <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
                        <div>
                             <h3 className="text-xl font-bold text-gray-800">
                                {selectedItem ? `${selectedItem.code ?? ""} ${selectedItem.title ?? ""}` : "รายละเอียด"}
                            </h3>
                        </div>
                        <button onClick={() => setModalOpen(false)} className="p-2 hover:bg-gray-200 rounded-full transition-colors text-gray-500">
                            <CloseIcon />
                        </button>
                    </div>

                     {/* Modal Body */}
                    <div className="p-6 overflow-y-auto">
                        {selectedItemKey && groupedDetails[selectedItemKey] && (
                            <>
                                <div className="flex justify-end mb-4">
                                     <button
                                        onClick={() => {
                                            const data = groupedDetails[selectedItemKey] || [];
                                            const transformedData = data.map((item) => ({
                                              วัน: new Date(item.poDate).toLocaleString("th-TH", {
                                                year: "numeric",
                                                month: "long",
                                                day: "2-digit",
                                                hour: "2-digit",
                                                minute: "2-digit",
                                              }),
                                              เลขที่ตั๋ว: item.poNumber,
                                              ลูกค้า: item.supplier,
                                              "น้ำหนัก (กก.)": (item.netWeight ?? 0).toFixed(2),
                                              ราคาต่อหน่วย: (item.pricePerUnit ?? 0).toFixed(2),
                                              "รวมก่อนภาษี (บาท)": (
                                                item.amountBeforeVat ??
                                                (item.netWeight || 0) * (item.pricePerUnit || 0)
                                              ).toFixed(2),
                                            }));
                                            exportToCSV(
                                              transformedData,
                                              `details-${selectedItemKey}.csv`
                                            );
                                        }}
                                        className="flex items-center gap-2 px-3 py-1.5 bg-white text-gray-700 border border-gray-200 rounded-lg hover:bg-gray-50 transition-all text-sm font-medium shadow-sm"
                                    >
                                        <ContentCopyIcon fontSize="small" />
                                        <span>Export</span>
                                    </button>
                                </div>
                                
                                <div className="rounded-xl border border-gray-200 overflow-hidden">
                                    <table className="w-full text-sm text-left">
                                        <thead className="bg-gray-50 text-gray-500 font-medium border-b border-gray-200">
                                            <tr>
                                                <th className="px-4 py-3">วันและเวลาจ่ายตั๋ว</th>
                                                <th className="px-4 py-3 text-right">เลขที่ตั๋ว</th>
                                                <th className="px-4 py-3">ลูกค้า</th>
                                                <th className="px-4 py-3 text-right">น้ำหนัก</th>
                                                <th className="px-4 py-3 text-right">ราคาต่อหน่วย</th>
                                                <th className="px-4 py-3 text-right">รวมก่อนภาษี</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-100">
                                            {groupedDetails[selectedItemKey].map((item, index) => (
                                                <tr key={index} className="hover:bg-gray-50">
                                                    <td className="px-4 py-3 text-gray-600">
                                                        {item.poDate && new Date(item.poDate).toLocaleString("th-TH", {
                                                            day: "numeric",
                                                            month: "long",
                                                            year: "numeric",
                                                            hour: "2-digit",
                                                            minute: "2-digit",
                                                            hour12: false,
                                                        }) + " น."}
                                                    </td>
                                                    <td className="px-4 py-3 text-right text-gray-800 font-medium">{item.poNumber}</td>
                                                    <td className="px-4 py-3 text-gray-600">{item.supplier}</td>
                                                    <td className="px-4 py-3 text-right text-gray-600">{formatNum(item.netWeight)}</td>
                                                    <td className="px-4 py-3 text-right text-gray-600">{formatNum(item.pricePerUnit)}</td>
                                                    <td className="px-4 py-3 text-right font-medium text-gray-800">
                                                        {formatNum(
                                                            item.amountBeforeVat ??
                                                            (item.netWeight || 0) * (item.pricePerUnit || 0)
                                                        )}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>
        )}
    </div>
    </div>
  );
}
